import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { PublicShell } from "@/components/public/public-shell";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { ChildPicker, type NewChildInput } from "@/components/tarbawiya/child-picker";
import { eyebrow, sectionTitle } from "@/components/ds/styles";
import { addChild, getTarbawiyaGate, listMyChildren } from "@/lib/tarbawiya/children.functions";
import { TARBAWIYA_UI as UI } from "@/lib/tarbawiya/ui-copy";

const childrenQuery = () =>
  queryOptions({ queryKey: ["tarbawiya", "children"], queryFn: () => listMyChildren(), gcTime: 0, retry: false });

export const Route = createFileRoute("/_authenticated/portal/tarbawiya")({
  beforeLoad: async () => {
    const gate = await getTarbawiyaGate();
    if (!gate.canView) throw redirect({ to: "/portal" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(childrenQuery()),
  gcTime: 0,
  head: () => ({
    meta: [
      { title: `${UI.title} — د. سارة النحّاس` },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: () => (
    <PublicShell ground="bg-cream-200">
      <TarbawiyaPage />
    </PublicShell>
  ),
});

function TarbawiyaPage() {
  const qc = useQueryClient();
  const initialData = Route.useLoaderData();
  const { data: children } = useSuspenseQuery({ ...childrenQuery(), initialData });
  const add = useServerFn(addChild);
  const [selected, setSelected] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (input: NewChildInput) => add({ data: input }),
    onSuccess: (child) => {
      setSelected(child.id);
      return qc.invalidateQueries({ queryKey: ["tarbawiya", "children"] });
    },
  });

  return (
    <div dir="rtl" className="mx-auto max-w-[44rem] px-5 py-10 lg:py-14">
      <p className={eyebrow}>{UI.title}</p>
      <h1 className={`${sectionTitle} mt-2`}>{UI.lead}</h1>
      <div className="mt-8">
        <ChildPicker
          children={children}
          selectedId={selected}
          onSelect={(c) => setSelected(c.id)}
          adding={m.isPending}
          onAdd={async (input) => {
            await m.mutateAsync(input);
          }}
        />
      </div>
    </div>
  );
}
